import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  IconButton,
  AppBar,
  Toolbar,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import API_CONFIG, { getApiUrl } from '../../config/api';
import { setClarityUser, sendClarityEvent,setClarityHost } from '../../utils/clarity';


// Use logo from public folder to avoid build path issues
const logo = process.env.PUBLIC_URL + '/logo.png';

// Placeholder images for templates
const getTemplateThumbnail = (filename) => {
  // In a real app, you would have actual thumbnails
  // For now, we'll use placeholder colors based on the template name
  const colors = {
    'customer_satisfaction.jpg': '#4CAF50',
    'event_feedback.jpg': '#2196F3',
    'employee_satisfaction.jpg': '#9C27B0',
    'product_feedback.jpg': '#FF5722',
    'course_evaluation.jpg': '#3F51B5'
  };

  const color = colors[filename] || '#607D8B';

  return (
    <Box
      sx={{
        height: 140,
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '1.25rem',
        fontWeight: 500
      }}
    >
      {filename.split('.')[0].split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')}
    </Box>
  );
};

const TemplatesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [surveyName, setSurveyName] = useState('');

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else {
        try {
          setClarityHost(null, currentUser);
          try {
            // set user-level properties in Clarity (no survey yet)
            setClarityUser(null, currentUser?.uid || currentUser?.id || null);
          } catch (e) {
            console.warn('Failed to set clarity user', e);
          }
      } catch (e) {
        console.warn('Failed to set clarity user', e);
      }

      fetchTemplates();
    }
  }, [currentUser, navigate]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.TEMPLATES.LIST));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch templates');
      }

      setTemplates(data.templates || []);
      setFilteredTemplates(data.templates || []);
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (!query && !selectedCategory) {
      setFilteredTemplates(templates);
      return;
    }

    let filtered = templates;

    // Apply search filter
    if (query) {
      const lowercaseQuery = query.toLowerCase();
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(lowercaseQuery) ||
        template.description.toLowerCase().includes(lowercaseQuery) ||
        template.category.toLowerCase().includes(lowercaseQuery)
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(template => template.category === selectedCategory);
    }

    setFilteredTemplates(filtered);
  };

  const handleCategoryFilter = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);

      // If there's a search query, filter by that only
      if (searchQuery) {
        handleSearch({ target: { value: searchQuery } });
      } else {
        setFilteredTemplates(templates);
      }
    } else {
      setSelectedCategory(category);

      let filtered = templates.filter(template => template.category === category);

      // Apply search filter if exists
      if (searchQuery) {
        const lowercaseQuery = searchQuery.toLowerCase();
        filtered = filtered.filter(template =>
          template.name.toLowerCase().includes(lowercaseQuery) ||
          template.description.toLowerCase().includes(lowercaseQuery)
        );
      }

      setFilteredTemplates(filtered);
    }
  };

  const handleTemplateSelect = async (template) => {
    try {
      setSelectedTemplate(template);
      setNameModalOpen(true);
    } catch (err) {
      console.error('Error selecting template:', err);
      setError(err.message);
    }
  };

  const handleCreateFromTemplate = async () => {
    try {
      // Create survey from template using the new endpoint
      const createResponse = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.CREATE_FROM_TEMPLATE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUser.uid,
          name: surveyName,
          template_id: selectedTemplate.id
        })
      });

      const createData = await createResponse.json();
      if (!createResponse.ok) {
        throw new Error(createData.message || 'Failed to create survey from template');
      }

      // Navigate to the survey editor
      navigate(`/survey/edit/${createData.survey_id}`);
    } catch (err) {
      console.error('Error creating survey from template:', err);
      setError(err.message);
    } finally {
      setNameModalOpen(false);
      setSurveyName('');
      setSelectedTemplate(null);
    }
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid',
          borderColor: 'grey.100'
        }}
      >
        <Toolbar sx={{ minHeight: '64px' }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ color: theme.palette.text.primary, mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src={logo}
              alt="SurveySlack Logo"
              style={{
                height: '32px',
                marginRight: '12px'
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '1.25rem',
                letterSpacing: '-0.025em'
              }}
            >
              Survey Templates
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ pt: 10, pb: 8 }}>
        {/* Search and Filters */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search templates..."
                variant="outlined"
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  <FilterIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Filter by:
                  </Typography>
                </Box>
                {categories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    clickable
                    color={selectedCategory === category ? "primary" : "default"}
                    onClick={() => handleCategoryFilter(category)}
                    sx={{
                      borderRadius: 1.5,
                      '&.MuiChip-colorDefault': {
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'grey.300'
                      }
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Templates Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : filteredTemplates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No templates found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredTemplates.map((template) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <CardActionArea
                    onClick={() => handleTemplateSelect(template)}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    {getTemplateThumbnail(template.thumbnail)}
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Typography variant="subtitle1" component="div" fontWeight={500} gutterBottom>
                        {template.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {template.description}
                      </Typography>
                      <Chip
                        label={template.category}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Add Name Dialog */}
      <Dialog
        open={nameModalOpen}
        onClose={() => setNameModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle>Name your survey</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Survey Name"
            type="text"
            fullWidth
            variant="outlined"
            value={surveyName}
            onChange={(e) => setSurveyName(e.target.value)}
            placeholder={selectedTemplate ? `${selectedTemplate.name} Survey` : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFromTemplate}
            variant="contained"
            disabled={!surveyName.trim()}
          >
            Create Survey
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesPage;